import { ArrowRight, Briefcase, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';

interface LoginProps {
  isBusy: boolean;
  onSignIn: () => void;
}

export default function Login({ isBusy, onSignIn }: LoginProps) {
  return (
    <div className="min-h-screen bg-linear-to-br from-background via-card to-background p-4 sm:p-6 md:p-8">
      <div className="mx-auto grid min-h-[calc(100vh-2rem)] max-w-6xl grid-cols-1 overflow-hidden rounded-2xl border border-border-subtle bg-card shadow-xl md:min-h-[calc(100vh-4rem)] lg:grid-cols-[1.15fr_0.85fr]">
        <section className="relative overflow-hidden px-6 py-8 sm:px-8 sm:py-10 md:px-12 md:py-14">
          <div className="absolute -top-20 -left-16 h-56 w-56 rounded-full bg-accent blur-3xl" />
          <div className="absolute -bottom-24 right-0 h-64 w-64 rounded-full bg-accent blur-3xl" />

          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 rounded-full border border-accent bg-card/90 px-3 py-1 text-sm font-bold uppercase tracking-[0.2em] text-accent-foreground">
              <ShieldCheck size={12} /> Secure Access
            </div>

            <h1 className="mt-5 text-4xl font-bold tracking-tight text-text-main sm:text-5xl md:text-6xl">
              Welcome to DI HR Platform
            </h1>
            <p className="mt-4 max-w-xl text-base leading-7 text-text-subtle sm:text-xl">
              Sign in with your Microsoft account to manage recruitment pipelines, job postings, AI
              CV analysis, and team collaboration in one secure workspace.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-border-subtle bg-accent/40 p-4">
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-accent-foreground">
                  <Briefcase size={13} /> Hiring Pipeline
                </div>
                <p className="mt-2 text-base text-text-main">
                  Track candidate flow from screening to offer with real-time visibility.
                </p>
              </div>
              <div className="rounded-xl border border-border-subtle bg-accent/40 p-4">
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-accent-foreground">
                  <Sparkles size={13} /> AI Assistant
                </div>
                <p className="mt-2 text-base text-text-main">
                  Analyze CVs and receive actionable recommendations instantly.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-border-subtle bg-card px-6 py-8 sm:px-8 sm:py-10 lg:border-t-0 lg:border-l lg:px-10 lg:py-12">
          <div className="mx-auto max-w-sm">
            <div className="mb-8">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-lg font-bold text-primary-foreground">
                D
              </div>
              <h2 className="mt-4 text-3xl font-bold text-text-main">Sign in</h2>
              <p className="mt-2 text-base text-text-subtle">Use Microsoft Entra ID to continue.</p>
            </div>

            <Button
              type="button"
              onClick={onSignIn}
              disabled={isBusy}
              variant="outline"
              className="flex w-full items-center justify-between rounded-xl border border-border-subtle bg-card px-4 py-3 text-left transition-colors hover:border-primary disabled:cursor-not-allowed disabled:opacity-60"
            >
              <div className="flex items-center gap-3">
                <div className="grid h-8 w-8 place-items-center rounded-md border border-border-subtle bg-card text-sm font-bold text-accent-foreground">
                  MS
                </div>
                <span className="text-base font-semibold text-text-main">
                  Continue with Microsoft
                </span>
              </div>
              <ArrowRight size={16} className="text-text-subtle" />
            </Button>

            <p className="mt-4 text-sm leading-5 text-text-subtle">
              By signing in, you agree to your organization policy and secure authentication
              requirements.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
